import {
  compareHuge,
  huge,
  isHugeNumber,
  parseHugeInput,
  type HugeNumber,
} from "@/shared/numbers";
import {
  MPM_ASSIGNMENT_IDS,
  type MpmAssignmentId,
} from "@/features/hoverIdle/mpm/mpmDefinitions";
import { HOV_CELL_COUNT } from "@/features/hoverIdle/core/hoverIdleState";
import {
  isHovGainUpgradeCellIndex,
  toHovGainUpgradeMpTargetKey,
  type HovGainUpgradeMpTarget,
  type HovGainUpgradeMpTargetKey,
} from "@/features/hoverIdle/mpm/mpmHovGainUpgradeTargets";
import {
  HOV_AMPLIFIER_ENTRY_IDS,
  type HovAmplifierEntryId,
} from "@/features/hoverIdle/hov/hovAmplifierModel";
import {
  NHOV_BRANCH_IDS,
  isNHovBranchId,
} from "@/features/hoverIdle/nHov/nHovBranches";
import type { NHovBranchId } from "@/features/hoverIdle/types";
import type { ExplorationMpSlotId } from "@/features/hoverIdle/exploration/explorationTypes";
import {
  EXPLORATION_MP_SLOT_SEQUENCE,
  getExplorationMpSlotDefinition,
  isExplorationMpSlotId,
} from "@/features/hoverIdle/exploration/explorationDefinitions";

export interface MpmState {
  readonly assignments: Readonly<Record<MpmAssignmentId, number>>;
  readonly gridUnlock: MpmGridUnlockState;
  readonly hovTargets: MpmHovTargetState;
  readonly hovGainUpgrades: MpmHovGainUpgradeState;
  readonly mhovExecution: MpmMHovExecutionState;
  readonly nhovExecution: MpmNHovExecutionState;
  readonly ohovExecution: MpmOHovExecutionState;
  readonly amplifierPanels: MpmAmplifierPanelState;
  readonly amplifierUpgrades: MpmAmplifierUpgradeState;
  readonly exploration: MpmExplorationState;
  readonly presets: readonly MpmPresetSnapshotSlot[];
  // MPM-wide "Assign All" latch shared across any future Assign All-aware
  // section (currently only Amp Panel). Monotonic once latched.
  readonly assignAllUnlocked: boolean;
}

export type MpmPresetSnapshotSlot = MpmPresetSnapshot | null;

export interface MpmPresetAssignmentSectionSnapshot<TAssignment> {
  readonly enabled: boolean;
  readonly assignments: readonly TAssignment[];
}

export interface MpmPresetSnapshot {
  readonly hovTargets: MpmPresetAssignmentSectionSnapshot<number>;
  readonly hovGainUpgrades: MpmPresetAssignmentSectionSnapshot<HovGainUpgradeMpTarget>;
  readonly amplifierPanels: MpmPresetAssignmentSectionSnapshot<HovAmplifierEntryId>;
  readonly amplifierUpgrades: MpmPresetAssignmentSectionSnapshot<HovAmplifierEntryId>;
  readonly mhovExecution: {
    readonly enabled: boolean;
    readonly assigned: boolean;
  };
  readonly nhovExecution: MpmPresetAssignmentSectionSnapshot<NHovBranchId>;
  readonly ohovExecution: {
    readonly enabled: boolean;
    readonly assigned: boolean;
  };
  readonly exploration: {
    readonly enabled: boolean;
    readonly assignedSlotIds: readonly ExplorationMpSlotId[];
  };
  // Legacy presets have no numeric-settings snapshot. Keep that distinction so
  // loading one does not invent values that were never saved.
  readonly numericSettings: MpmPresetNumericSettings | null;
}

export interface MpmPresetNumericSettings {
  readonly mhovExecution: {
    readonly multiplier: HugeNumber;
    readonly minimumResetIntervalMs: number;
  };
  readonly nhovExecution: {
    readonly conditions: MpmNHovExecutionConditions;
  };
  readonly ohovExecution: {
    readonly minimumHovCore: HugeNumber;
    readonly minimumIntervalMs: number;
  };
}

export interface MpmAssignmentSectionState<TAssignment> {
  readonly unlocked: boolean;
  readonly enabled: boolean;
  readonly assignments: readonly TAssignment[];
}

export type MpmHovTargetState = MpmAssignmentSectionState<number>;

export type MpmHovGainUpgradeState =
  MpmAssignmentSectionState<HovGainUpgradeMpTarget>;

export type MpmAmplifierPanelState =
  MpmAssignmentSectionState<HovAmplifierEntryId>;

export type MpmAmplifierUpgradeState =
  MpmAssignmentSectionState<HovAmplifierEntryId>;

export interface MpmExplorationState {
  readonly enabled: boolean;
  readonly assignedSlotIds: readonly ExplorationMpSlotId[];
}

export interface MpmMHovExecutionState {
  readonly unlocked: boolean;
  readonly enabled: boolean;
  readonly assigned: boolean;
  readonly multiplier: HugeNumber;
  readonly minimumResetIntervalMs: number;
  readonly runtime: MpmMHovExecutionRuntimeState;
}

export interface MpmNHovExecutionState {
  readonly unlocked: boolean;
  readonly enabled: boolean;
  readonly assignments: readonly NHovBranchId[];
  readonly conditions: MpmNHovExecutionConditions;
}

export interface MpmOHovExecutionState {
  readonly unlocked: boolean;
  readonly enabled: boolean;
  readonly assigned: boolean;
  readonly minimumHovCore: HugeNumber;
  readonly minimumIntervalMs: number;
}

export interface MpmMHovExecutionRuntimeState {
  readonly pending: boolean;
}

export interface MpmNHovExecutionConditionState {
  readonly minimumEffect: HugeNumber;
  readonly minimumTimeSeconds: number;
}

export type MpmNHovExecutionConditions = Readonly<
  Record<NHovBranchId, MpmNHovExecutionConditionState>
>;

export const MPM_MHOV_EXECUTION_MP_COST = 3;
export const MPM_NHOV_EXECUTION_MP_COST_PER_BRANCH = 3;
export const MPM_OHOV_EXECUTION_MP_COST = 6;
export const MPM_PRESET_BASE_SLOT_COUNT = 2;
export const MPM_PRESET_MAX_SLOT_COUNT = 3;
export const MPM_MHOV_EXECUTION_MIN_MULTIPLIER = huge(1.1);
export const MPM_MHOV_EXECUTION_DEFAULT_MULTIPLIER = huge(1.1);
export const MPM_MHOV_EXECUTION_DEFAULT_MINIMUM_RESET_INTERVAL_MS = 0;
export const MPM_MHOV_EXECUTION_MAX_MINIMUM_RESET_INTERVAL_MS =
  365 * 24 * 60 * 60 * 1000;
export const MPM_NHOV_EXECUTION_MINIMUM_EFFECT =
  MPM_MHOV_EXECUTION_MIN_MULTIPLIER;
export const MPM_NHOV_EXECUTION_DEFAULT_MINIMUM_EFFECT =
  MPM_MHOV_EXECUTION_DEFAULT_MULTIPLIER;
export const MPM_NHOV_EXECUTION_DEFAULT_MINIMUM_TIME_SECONDS = 0;
export const MPM_NHOV_EXECUTION_MAX_MINIMUM_TIME_SECONDS = 365 * 24 * 60 * 60;
export const MPM_OHOV_EXECUTION_MINIMUM_HOV_CORE = huge(1);
export const MPM_OHOV_EXECUTION_DEFAULT_MINIMUM_HOV_CORE = huge(1);

const HOV_AMPLIFIER_ENTRY_ID_SET = new Set<HovAmplifierEntryId>(
  HOV_AMPLIFIER_ENTRY_IDS,
);

export interface MpmGridUnlockState {
  readonly hov: MpmHovGridUnlockState;
}

export interface MpmHovGridUnlockState {
  readonly gridIncrease1: boolean;
  readonly gridIncrease2: boolean;
}

export function createDefaultMpmState(): MpmState {
  return {
    assignments: createEmptyAssignments(),
    gridUnlock: createDefaultMpmGridUnlockState(),
    hovTargets: createDefaultMpmHovTargetState(),
    hovGainUpgrades: createDefaultMpmHovGainUpgradeState(),
    mhovExecution: createDefaultMpmMHovExecutionState(),
    nhovExecution: createDefaultMpmNHovExecutionState(),
    ohovExecution: createDefaultMpmOHovExecutionState(),
    amplifierPanels: createDefaultMpmAmplifierPanelState(),
    amplifierUpgrades: createDefaultMpmAmplifierUpgradeState(),
    exploration: createDefaultMpmExplorationState(),
    presets: createDefaultMpmPresetSnapshotSlots(),
    assignAllUnlocked: false,
  };
}

export function createDefaultMpmGridUnlockState(): MpmGridUnlockState {
  return {
    hov: {
      gridIncrease1: false,
      gridIncrease2: false,
    },
  };
}

export function cloneMpmState(state: MpmState): MpmState {
  return normalizeMpmState(state);
}

export function normalizeMpmState(state: unknown): MpmState {
  const stateRecord = isRecord(state) ? state : {};
  return {
    assignments: normalizeSupportAssignments(stateRecord.assignments),
    gridUnlock: normalizeMpmGridUnlockState(stateRecord.gridUnlock),
    hovTargets: normalizeMpmHovTargetState(stateRecord.hovTargets),
    hovGainUpgrades: normalizeMpmHovGainUpgradeState(
      stateRecord.hovGainUpgrades,
    ),
    mhovExecution: normalizeMpmMHovExecutionState(stateRecord.mhovExecution),
    nhovExecution: normalizeMpmNHovExecutionState(stateRecord.nhovExecution),
    ohovExecution: normalizeMpmOHovExecutionState(stateRecord.ohovExecution),
    amplifierPanels: normalizeMpmAmplifierPanelState(
      stateRecord.amplifierPanels,
    ),
    amplifierUpgrades: normalizeMpmAmplifierUpgradeState(
      stateRecord.amplifierUpgrades,
    ),
    exploration: normalizeMpmExplorationState(stateRecord.exploration),
    presets: normalizeMpmPresetSnapshotSlots(stateRecord.presets),
    assignAllUnlocked: stateRecord.assignAllUnlocked === true,
  };
}

export function withMpmAssignAllUnlockedLatched(
  state: Partial<MpmState> | undefined,
  shouldLatch: boolean,
): MpmState {
  const normalized = normalizeMpmState(state);
  if (!shouldLatch || normalized.assignAllUnlocked) {
    return normalized;
  }
  return {
    ...normalized,
    assignAllUnlocked: true,
  };
}

export function withMpmHovGridUnlockLatched(
  state: Partial<MpmState> | undefined,
  unlocks: MpmHovGridUnlockState,
): MpmState {
  const normalized = normalizeMpmState(state);
  const next: MpmHovGridUnlockState = {
    gridIncrease1:
      normalized.gridUnlock.hov.gridIncrease1 || unlocks.gridIncrease1,
    gridIncrease2:
      normalized.gridUnlock.hov.gridIncrease2 || unlocks.gridIncrease2,
  };
  if (
    next.gridIncrease1 === normalized.gridUnlock.hov.gridIncrease1 &&
    next.gridIncrease2 === normalized.gridUnlock.hov.gridIncrease2
  ) {
    return normalized;
  }
  return {
    ...normalized,
    gridUnlock: { hov: next },
  };
}

export function clampMpmAssignmentsToCapacity(
  state: Partial<MpmState> | undefined,
  mpCapacity: number,
): MpmState {
  const normalized = normalizeMpmState(state);
  const capacity = isNonNegativeInteger(mpCapacity) ? mpCapacity : 0;
  const hovTargetAssignments = normalized.hovTargets.assignments.slice(
    0,
    capacity,
  );
  const remainingForHovGainUpgrades = Math.max(
    0,
    capacity - hovTargetAssignments.length,
  );
  const hovGainUpgradeAssignments =
    normalized.hovGainUpgrades.assignments.slice(
      0,
      remainingForHovGainUpgrades,
    );
  const remainingAfterHovGainUpgrades = Math.max(
    0,
    remainingForHovGainUpgrades - hovGainUpgradeAssignments.length,
  );
  const mhovExecutionAssigned =
    normalized.mhovExecution.assigned &&
    remainingAfterHovGainUpgrades >= MPM_MHOV_EXECUTION_MP_COST;
  const remainingAfterMHov = Math.max(
    0,
    remainingAfterHovGainUpgrades -
      (mhovExecutionAssigned ? MPM_MHOV_EXECUTION_MP_COST : 0),
  );
  const maxNHovBranchAssignments = Math.floor(
    remainingAfterMHov / MPM_NHOV_EXECUTION_MP_COST_PER_BRANCH,
  );
  const nhovExecutionAssignments = normalized.nhovExecution.assignments.slice(
    0,
    maxNHovBranchAssignments,
  );
  const remainingAfterNHov = Math.max(
    0,
    remainingAfterMHov -
      nhovExecutionAssignments.length * MPM_NHOV_EXECUTION_MP_COST_PER_BRANCH,
  );
  const ohovExecutionAssigned =
    normalized.ohovExecution.assigned &&
    remainingAfterNHov >= MPM_OHOV_EXECUTION_MP_COST;
  const remainingForAmplifierPanelsAfterOHov = Math.max(
    0,
    remainingAfterNHov -
      (ohovExecutionAssigned ? MPM_OHOV_EXECUTION_MP_COST : 0),
  );
  const amplifierPanelAssignments =
    normalized.amplifierPanels.assignments.slice(
      0,
      remainingForAmplifierPanelsAfterOHov,
    );
  const remainingForAmplifierUpgrades = Math.max(
    0,
    remainingForAmplifierPanelsAfterOHov - amplifierPanelAssignments.length,
  );
  const amplifierUpgradeAssignments =
    normalized.amplifierUpgrades.assignments.slice(
      0,
      remainingForAmplifierUpgrades,
    );
  let remainingForExploration = Math.max(
    0,
    remainingForAmplifierUpgrades - amplifierUpgradeAssignments.length,
  );
  const explorationAssignedSlotIds: ExplorationMpSlotId[] = [];
  for (const slotId of normalized.exploration.assignedSlotIds) {
    const amount = getExplorationMpSlotDefinition(slotId).amount;
    if (amount > remainingForExploration) continue;
    explorationAssignedSlotIds.push(slotId);
    remainingForExploration -= amount;
  }

  return {
    ...normalized,
    hovTargets: {
      ...normalized.hovTargets,
      assignments: hovTargetAssignments,
    },
    hovGainUpgrades: {
      ...normalized.hovGainUpgrades,
      assignments: hovGainUpgradeAssignments,
    },
    mhovExecution: {
      ...normalized.mhovExecution,
      assigned: mhovExecutionAssigned,
      runtime: createDefaultMpmMHovExecutionRuntimeState(),
    },
    nhovExecution: {
      ...normalized.nhovExecution,
      assignments: nhovExecutionAssignments,
    },
    ohovExecution: {
      ...normalized.ohovExecution,
      assigned: ohovExecutionAssigned,
    },
    amplifierPanels: {
      ...normalized.amplifierPanels,
      assignments: amplifierPanelAssignments,
    },
    amplifierUpgrades: {
      ...normalized.amplifierUpgrades,
      assignments: amplifierUpgradeAssignments,
    },
    exploration: {
      ...normalized.exploration,
      assignedSlotIds: explorationAssignedSlotIds,
    },
  };
}

export type MpmCapacityReconcileReason =
  | "capacity-change"
  | "phov-reset"
  | "progress-builder-apply";

export interface MpmCapacityReconcileOptions {
  readonly mpCapacity: number;
  // Auxiliary tag for logs/diagnostics only. The strip priority is shared
  // across every reason and is decided by clampMpmAssignmentsToCapacity, not by
  // this value.
  readonly reason: MpmCapacityReconcileReason;
}

// Shared MP capacity reconciliation entry point. PHov-driven capacity changes
// and Progress Builder apply both reconcile through here so over-capacity
// assignments are stripped by the same priority; `reason` does not change what
// is stripped.
export function reconcileMpmAssignmentsForCapacity(
  state: Partial<MpmState> | undefined,
  options: MpmCapacityReconcileOptions,
): MpmState {
  return clampMpmAssignmentsToCapacity(state, options.mpCapacity);
}

export interface MpmAssignmentSectionIntent<TAssignment> {
  readonly enabled: boolean;
  readonly assignments: readonly TAssignment[];
}

// User-authored MP placement and execution setup, captured so a progress
// rebuild (Progress Builder apply) can restart progression without discarding
// the MP layout the user arranged for verification. Section `unlocked`, MP
// capacity, execution runtime scheduling, and active/inactive resolution are
// deliberately excluded: those are re-derived from the post-apply progress
// state, while this intent is overlaid on top.
export interface MpmAssignmentIntent {
  readonly hovTargets: MpmAssignmentSectionIntent<number>;
  readonly hovGainUpgrades: MpmAssignmentSectionIntent<HovGainUpgradeMpTarget>;
  readonly amplifierPanels: MpmAssignmentSectionIntent<HovAmplifierEntryId>;
  readonly amplifierUpgrades: MpmAssignmentSectionIntent<HovAmplifierEntryId>;
  readonly mhovExecution: {
    readonly enabled: boolean;
    readonly assigned: boolean;
    readonly multiplier: HugeNumber;
    readonly minimumResetIntervalMs: number;
  };
  readonly nhovExecution: {
    readonly enabled: boolean;
    readonly assignments: readonly NHovBranchId[];
    readonly conditions: MpmNHovExecutionConditions;
  };
  readonly ohovExecution: {
    readonly enabled: boolean;
    readonly assigned: boolean;
    readonly minimumHovCore: HugeNumber;
    readonly minimumIntervalMs: number;
  };
  readonly supportAssignments: Readonly<Record<MpmAssignmentId, number>>;
  readonly exploration: MpmExplorationState;
}

export function captureMpmAssignmentIntent(
  state: Partial<MpmState> | undefined,
): MpmAssignmentIntent {
  const normalized = normalizeMpmState(state);
  return {
    hovTargets: {
      enabled: normalized.hovTargets.enabled,
      assignments: normalized.hovTargets.assignments,
    },
    hovGainUpgrades: {
      enabled: normalized.hovGainUpgrades.enabled,
      assignments: normalized.hovGainUpgrades.assignments,
    },
    amplifierPanels: {
      enabled: normalized.amplifierPanels.enabled,
      assignments: normalized.amplifierPanels.assignments,
    },
    amplifierUpgrades: {
      enabled: normalized.amplifierUpgrades.enabled,
      assignments: normalized.amplifierUpgrades.assignments,
    },
    mhovExecution: {
      enabled: normalized.mhovExecution.enabled,
      assigned: normalized.mhovExecution.assigned,
      multiplier: normalized.mhovExecution.multiplier,
      minimumResetIntervalMs: normalized.mhovExecution.minimumResetIntervalMs,
    },
    nhovExecution: {
      enabled: normalized.nhovExecution.enabled,
      assignments: normalized.nhovExecution.assignments,
      conditions: normalized.nhovExecution.conditions,
    },
    ohovExecution: {
      enabled: normalized.ohovExecution.enabled,
      assigned: normalized.ohovExecution.assigned,
      minimumHovCore: normalized.ohovExecution.minimumHovCore,
      minimumIntervalMs: normalized.ohovExecution.minimumIntervalMs,
    },
    supportAssignments: normalized.assignments,
    exploration: normalized.exploration,
  };
}

// Overlays captured assignment intent onto a freshly-built progress state.
// `progressState` supplies the re-derived unlock / grid / assign-all / runtime
// fields; `intent` supplies the user's MP placement and execution setup. The
// result may exceed MP capacity, so callers reconcile afterward with
// reconcileMpmAssignmentsForCapacity to strip any overflow.
export function overlayMpmAssignmentIntent(
  progressState: Partial<MpmState> | undefined,
  intent: MpmAssignmentIntent,
): MpmState {
  const base = normalizeMpmState(progressState);
  return normalizeMpmState({
    ...base,
    assignments: intent.supportAssignments,
    exploration: intent.exploration,
    hovTargets: {
      ...base.hovTargets,
      enabled: intent.hovTargets.enabled,
      assignments: intent.hovTargets.assignments,
    },
    hovGainUpgrades: {
      ...base.hovGainUpgrades,
      enabled: intent.hovGainUpgrades.enabled,
      assignments: intent.hovGainUpgrades.assignments,
    },
    amplifierPanels: {
      ...base.amplifierPanels,
      enabled: intent.amplifierPanels.enabled,
      assignments: intent.amplifierPanels.assignments,
    },
    amplifierUpgrades: {
      ...base.amplifierUpgrades,
      enabled: intent.amplifierUpgrades.enabled,
      assignments: intent.amplifierUpgrades.assignments,
    },
    mhovExecution: {
      ...base.mhovExecution,
      enabled: intent.mhovExecution.enabled,
      assigned: intent.mhovExecution.assigned,
      multiplier: intent.mhovExecution.multiplier,
      minimumResetIntervalMs: intent.mhovExecution.minimumResetIntervalMs,
    },
    nhovExecution: {
      ...base.nhovExecution,
      enabled: intent.nhovExecution.enabled,
      assignments: intent.nhovExecution.assignments,
      conditions: intent.nhovExecution.conditions,
    },
    ohovExecution: {
      ...base.ohovExecution,
      enabled: intent.ohovExecution.enabled,
      assigned: intent.ohovExecution.assigned,
      minimumHovCore: intent.ohovExecution.minimumHovCore,
      minimumIntervalMs: intent.ohovExecution.minimumIntervalMs,
    },
  });
}

export function withMpmHovGainUpgradesUnlocked(
  state: Partial<MpmState> | undefined,
  unlocked: boolean,
): MpmState {
  const normalized = normalizeMpmState(state);
  return {
    ...normalized,
    hovGainUpgrades: {
      ...normalized.hovGainUpgrades,
      unlocked,
    },
  };
}

export function withMpmAmplifierPanelsUnlocked(
  state: Partial<MpmState> | undefined,
  unlocked: boolean,
): MpmState {
  const normalized = normalizeMpmState(state);
  return {
    ...normalized,
    amplifierPanels: {
      ...normalized.amplifierPanels,
      unlocked,
    },
  };
}

export function withMpmAmplifierUpgradesUnlocked(
  state: Partial<MpmState> | undefined,
  unlocked: boolean,
): MpmState {
  const normalized = normalizeMpmState(state);
  return {
    ...normalized,
    amplifierUpgrades: {
      ...normalized.amplifierUpgrades,
      unlocked,
    },
  };
}

export function withMpmMHovExecutionUnlocked(
  state: Partial<MpmState> | undefined,
  unlocked: boolean,
): MpmState {
  const normalized = normalizeMpmState(state);
  return {
    ...normalized,
    mhovExecution: {
      ...normalized.mhovExecution,
      unlocked,
    },
  };
}

export function withMpmNHovExecutionUnlocked(
  state: Partial<MpmState> | undefined,
  unlocked: boolean,
): MpmState {
  const normalized = normalizeMpmState(state);
  return {
    ...normalized,
    nhovExecution: {
      ...normalized.nhovExecution,
      unlocked,
    },
  };
}

export function withMpmOHovExecutionUnlocked(
  state: Partial<MpmState> | undefined,
  unlocked: boolean,
): MpmState {
  const normalized = normalizeMpmState(state);
  return {
    ...normalized,
    ohovExecution: {
      ...normalized.ohovExecution,
      unlocked,
    },
  };
}

function createDefaultMpmHovTargetState(): MpmHovTargetState {
  return {
    unlocked: true,
    enabled: true,
    assignments: [],
  };
}

function createDefaultMpmHovGainUpgradeState(): MpmHovGainUpgradeState {
  return {
    unlocked: false,
    enabled: true,
    assignments: [],
  };
}

function createDefaultMpmAmplifierPanelState(): MpmAmplifierPanelState {
  return {
    unlocked: false,
    enabled: true,
    assignments: [],
  };
}

function createDefaultMpmAmplifierUpgradeState(): MpmAmplifierUpgradeState {
  return {
    unlocked: false,
    enabled: true,
    assignments: [],
  };
}

function createDefaultMpmExplorationState(): MpmExplorationState {
  return { enabled: true, assignedSlotIds: [] };
}

function createDefaultMpmPresetSnapshotSlots(): MpmPresetSnapshotSlot[] {
  return Array.from<MpmPresetSnapshotSlot>({
    length: MPM_PRESET_MAX_SLOT_COUNT,
  }).fill(null);
}

function normalizeMpmPresetSnapshotSlots(
  value: unknown,
): MpmPresetSnapshotSlot[] {
  const source = Array.isArray(value) ? value : [];
  return Array.from({ length: MPM_PRESET_MAX_SLOT_COUNT }, (_, index) =>
    normalizeMpmPresetSnapshot(source[index]),
  );
}

function normalizeMpmPresetSnapshot(value: unknown): MpmPresetSnapshot | null {
  if (!isRecord(value)) return null;
  const hovTargets = normalizeMpmHovTargetState(value.hovTargets);
  const hovGainUpgrades = normalizeMpmHovGainUpgradeState(
    value.hovGainUpgrades,
  );
  const amplifierPanels = normalizeMpmAmplifierPanelState(
    value.amplifierPanels,
  );
  const amplifierUpgrades = normalizeMpmAmplifierUpgradeState(
    value.amplifierUpgrades,
  );
  const mhovExecution = normalizeMpmMHovExecutionState(value.mhovExecution);
  const nhovExecution = normalizeMpmNHovExecutionState(value.nhovExecution);
  const ohovExecution = normalizeMpmOHovExecutionState(value.ohovExecution);
  const exploration = normalizeMpmExplorationState(value.exploration);
  return {
    hovTargets: {
      enabled: hovTargets.enabled,
      assignments: hovTargets.assignments,
    },
    hovGainUpgrades: {
      enabled: hovGainUpgrades.enabled,
      assignments: hovGainUpgrades.assignments,
    },
    amplifierPanels: {
      enabled: amplifierPanels.enabled,
      assignments: amplifierPanels.assignments,
    },
    amplifierUpgrades: {
      enabled: amplifierUpgrades.enabled,
      assignments: amplifierUpgrades.assignments,
    },
    mhovExecution: {
      enabled: mhovExecution.enabled,
      assigned: mhovExecution.assigned,
    },
    nhovExecution: {
      enabled: nhovExecution.enabled,
      assignments: nhovExecution.assignments,
    },
    ohovExecution: {
      enabled: ohovExecution.enabled,
      assigned: ohovExecution.assigned,
    },
    exploration: {
      enabled: exploration.enabled,
      assignedSlotIds: exploration.assignedSlotIds,
    },
    numericSettings: normalizeMpmPresetNumericSettings(value.numericSettings),
  };
}

function normalizeMpmPresetNumericSettings(
  value: unknown,
): MpmPresetNumericSettings | null {
  if (!isRecord(value)) return null;
  const defaults = createDefaultMpmState();
  const record = value;
  const mhovExecution = normalizeMpmMHovExecutionState({
    ...defaults.mhovExecution,
    ...(isRecord(record.mhovExecution) ? record.mhovExecution : {}),
  });
  const nhovExecution = normalizeMpmNHovExecutionState({
    ...defaults.nhovExecution,
    ...(isRecord(record.nhovExecution) ? record.nhovExecution : {}),
  });
  const ohovExecution = normalizeMpmOHovExecutionState({
    ...defaults.ohovExecution,
    ...(isRecord(record.ohovExecution) ? record.ohovExecution : {}),
  });
  return {
    mhovExecution: {
      multiplier: mhovExecution.multiplier,
      minimumResetIntervalMs: mhovExecution.minimumResetIntervalMs,
    },
    nhovExecution: {
      conditions: nhovExecution.conditions,
    },
    ohovExecution: {
      minimumHovCore: ohovExecution.minimumHovCore,
      minimumIntervalMs: ohovExecution.minimumIntervalMs,
    },
  };
}

function normalizeMpmExplorationState(value: unknown): MpmExplorationState {
  const record = isRecord(value) ? value : {};
  const source = Array.isArray(record.assignedSlotIds)
    ? record.assignedSlotIds
    : [];
  const selected = new Set<ExplorationMpSlotId>();
  for (const candidate of source) {
    if (isExplorationMpSlotId(candidate)) {
      selected.add(candidate);
    }
  }
  return {
    enabled: typeof record.enabled === "boolean" ? record.enabled : true,
    assignedSlotIds: EXPLORATION_MP_SLOT_SEQUENCE.map((slot) => slot.id).filter(
      (slotId) => selected.has(slotId),
    ),
  };
}

function createDefaultMpmMHovExecutionState(): MpmMHovExecutionState {
  return {
    unlocked: false,
    enabled: true,
    assigned: false,
    multiplier: MPM_MHOV_EXECUTION_DEFAULT_MULTIPLIER,
    minimumResetIntervalMs:
      MPM_MHOV_EXECUTION_DEFAULT_MINIMUM_RESET_INTERVAL_MS,
    runtime: createDefaultMpmMHovExecutionRuntimeState(),
  };
}

function createDefaultMpmNHovExecutionState(): MpmNHovExecutionState {
  return {
    unlocked: false,
    enabled: true,
    assignments: [],
    conditions: createDefaultMpmNHovExecutionConditions(),
  };
}

function createDefaultMpmOHovExecutionState(): MpmOHovExecutionState {
  return {
    unlocked: false,
    enabled: true,
    assigned: false,
    minimumHovCore: MPM_OHOV_EXECUTION_DEFAULT_MINIMUM_HOV_CORE,
    minimumIntervalMs: MPM_MHOV_EXECUTION_DEFAULT_MINIMUM_RESET_INTERVAL_MS,
  };
}

function createDefaultMpmMHovExecutionRuntimeState(): MpmMHovExecutionRuntimeState {
  return { pending: false };
}

function normalizeSupportAssignments(
  source: unknown,
): Record<MpmAssignmentId, number> {
  const assignments = createEmptyAssignments();
  if (!isRecord(source)) {
    return assignments;
  }

  for (const assignmentId of MPM_ASSIGNMENT_IDS) {
    const value = source[assignmentId];
    assignments[assignmentId] = isNonNegativeInteger(value) ? value : 0;
  }

  return assignments;
}

function createEmptyAssignments(): Record<MpmAssignmentId, number> {
  return Object.fromEntries(
    MPM_ASSIGNMENT_IDS.map((assignmentId) => [assignmentId, 0]),
  ) as Record<MpmAssignmentId, number>;
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

function normalizeMpmHovTargetState(value: unknown): MpmHovTargetState {
  const defaults = createDefaultMpmHovTargetState();
  const record = isRecord(value) ? value : {};
  return {
    unlocked:
      typeof record.unlocked === "boolean"
        ? record.unlocked
        : defaults.unlocked,
    enabled:
      typeof record.enabled === "boolean" ? record.enabled : defaults.enabled,
    assignments: normalizeHovTargetAssignments(record.assignments),
  };
}

function normalizeMpmGridUnlockState(value: unknown): MpmGridUnlockState {
  if (!isRecord(value)) {
    return createDefaultMpmGridUnlockState();
  }
  const hov = isRecord(value.hov) ? value.hov : {};
  return {
    hov: {
      gridIncrease1: hov.gridIncrease1 === true,
      gridIncrease2: hov.gridIncrease2 === true,
    },
  };
}

function normalizeMpmHovGainUpgradeState(
  value: unknown,
): MpmHovGainUpgradeState {
  const defaults = createDefaultMpmHovGainUpgradeState();
  const record = isRecord(value) ? value : {};
  return {
    unlocked:
      typeof record.unlocked === "boolean"
        ? record.unlocked
        : defaults.unlocked,
    enabled:
      typeof record.enabled === "boolean" ? record.enabled : defaults.enabled,
    assignments: normalizeHovGainUpgradeAssignments(record.assignments),
  };
}

function normalizeMpmAmplifierPanelState(
  value: unknown,
): MpmAmplifierPanelState {
  const defaults = createDefaultMpmAmplifierPanelState();
  const record = isRecord(value) ? value : {};
  return {
    unlocked:
      typeof record.unlocked === "boolean"
        ? record.unlocked
        : defaults.unlocked,
    enabled:
      typeof record.enabled === "boolean" ? record.enabled : defaults.enabled,
    assignments: normalizeAmplifierPanelAssignments(record.assignments),
  };
}

function normalizeMpmAmplifierUpgradeState(
  value: unknown,
): MpmAmplifierUpgradeState {
  const defaults = createDefaultMpmAmplifierUpgradeState();
  const record = isRecord(value) ? value : {};
  return {
    unlocked:
      typeof record.unlocked === "boolean"
        ? record.unlocked
        : defaults.unlocked,
    enabled:
      typeof record.enabled === "boolean" ? record.enabled : defaults.enabled,
    assignments: normalizeAmplifierEntryAssignments(record.assignments),
  };
}

function normalizeMpmMHovExecutionState(value: unknown): MpmMHovExecutionState {
  const defaults = createDefaultMpmMHovExecutionState();
  const record = isRecord(value) ? value : {};
  const multiplier = normalizeMpmMHovExecutionMultiplier(record.multiplier);
  return {
    unlocked:
      typeof record.unlocked === "boolean"
        ? record.unlocked
        : defaults.unlocked,
    enabled:
      typeof record.enabled === "boolean" ? record.enabled : defaults.enabled,
    assigned: record.assigned === true,
    multiplier,
    minimumResetIntervalMs: normalizeMpmMHovExecutionMinimumResetIntervalMs(
      record.minimumResetIntervalMs,
    ),
    runtime: normalizeMpmMHovExecutionRuntimeState(record.runtime),
  };
}

function normalizeMpmNHovExecutionState(value: unknown): MpmNHovExecutionState {
  const defaults = createDefaultMpmNHovExecutionState();
  const record = isRecord(value) ? value : {};
  return {
    unlocked:
      typeof record.unlocked === "boolean"
        ? record.unlocked
        : defaults.unlocked,
    enabled:
      typeof record.enabled === "boolean" ? record.enabled : defaults.enabled,
    assignments: normalizeNHovExecutionAssignments(record.assignments),
    conditions: normalizeMpmNHovExecutionConditions(record.conditions),
  };
}

function normalizeMpmOHovExecutionState(value: unknown): MpmOHovExecutionState {
  const defaults = createDefaultMpmOHovExecutionState();
  const record = isRecord(value) ? value : {};
  return {
    unlocked:
      typeof record.unlocked === "boolean"
        ? record.unlocked
        : defaults.unlocked,
    enabled:
      typeof record.enabled === "boolean" ? record.enabled : defaults.enabled,
    assigned: record.assigned === true,
    minimumHovCore: normalizeMpmOHovExecutionMinimumHovCore(
      record.minimumHovCore,
    ),
    minimumIntervalMs: normalizeMpmMHovExecutionMinimumResetIntervalMs(
      record.minimumIntervalMs,
    ),
  };
}

export function normalizeMpmMHovExecutionMultiplier(
  value: unknown,
  fallback: HugeNumber = MPM_MHOV_EXECUTION_DEFAULT_MULTIPLIER,
): HugeNumber {
  const normalized = normalizeMpmExecutionHugeInput(value);
  if (normalized === null) {
    return fallback;
  }
  if (compareHuge(normalized, MPM_MHOV_EXECUTION_MIN_MULTIPLIER) < 0) {
    return MPM_MHOV_EXECUTION_MIN_MULTIPLIER;
  }
  return normalized;
}

export function normalizeMpmNHovExecutionMinimumEffect(
  value: unknown,
  fallback: HugeNumber = MPM_NHOV_EXECUTION_DEFAULT_MINIMUM_EFFECT,
): HugeNumber {
  return normalizeMpmExecutionHugeCondition({
    value,
    fallback,
    minimum: MPM_NHOV_EXECUTION_MINIMUM_EFFECT,
  });
}

export function normalizeMpmOHovExecutionMinimumHovCore(
  value: unknown,
  fallback: HugeNumber = MPM_OHOV_EXECUTION_DEFAULT_MINIMUM_HOV_CORE,
): HugeNumber {
  return normalizeMpmExecutionHugeCondition({
    value,
    fallback,
    minimum: MPM_OHOV_EXECUTION_MINIMUM_HOV_CORE,
  });
}

function normalizeMpmExecutionHugeCondition(options: {
  readonly value: unknown;
  readonly fallback: HugeNumber;
  readonly minimum: HugeNumber;
}): HugeNumber {
  const normalized = normalizeMpmExecutionHugeInput(options.value);
  if (normalized === null) {
    return options.fallback;
  }
  if (compareHuge(normalized, options.minimum) < 0) {
    return options.minimum;
  }
  return normalized;
}

function normalizeMpmExecutionHugeInput(value: unknown): HugeNumber | null {
  if (isHugeNumber(value)) {
    return value.isFinite() ? value : null;
  }
  if (typeof value === "string") {
    return parseHugeInput(value);
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return huge(value);
  }
  return null;
}

export function normalizeMpmMHovExecutionMinimumResetIntervalMs(
  value: unknown,
  fallback = MPM_MHOV_EXECUTION_DEFAULT_MINIMUM_RESET_INTERVAL_MS,
): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return fallback;
  }
  const integer = Math.trunc(value);
  if (integer < 0) return MPM_MHOV_EXECUTION_DEFAULT_MINIMUM_RESET_INTERVAL_MS;
  return Math.min(integer, MPM_MHOV_EXECUTION_MAX_MINIMUM_RESET_INTERVAL_MS);
}

export function normalizeMpmNHovExecutionMinimumTimeSeconds(
  value: unknown,
  fallback = MPM_NHOV_EXECUTION_DEFAULT_MINIMUM_TIME_SECONDS,
): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return fallback;
  }
  if (value < 0) return MPM_NHOV_EXECUTION_DEFAULT_MINIMUM_TIME_SECONDS;
  return Math.min(value, MPM_NHOV_EXECUTION_MAX_MINIMUM_TIME_SECONDS);
}

function normalizeMpmMHovExecutionRuntimeState(
  value: unknown,
): MpmMHovExecutionRuntimeState {
  const record = isRecord(value) ? value : {};
  return { pending: record.pending === true };
}

function createDefaultMpmNHovExecutionConditionState(): MpmNHovExecutionConditionState {
  return {
    minimumEffect: MPM_NHOV_EXECUTION_DEFAULT_MINIMUM_EFFECT,
    minimumTimeSeconds: MPM_NHOV_EXECUTION_DEFAULT_MINIMUM_TIME_SECONDS,
  };
}

function createDefaultMpmNHovExecutionConditions(): Record<
  NHovBranchId,
  MpmNHovExecutionConditionState
> {
  return Object.fromEntries(
    NHOV_BRANCH_IDS.map((branchId) => [
      branchId,
      createDefaultMpmNHovExecutionConditionState(),
    ]),
  ) as Record<NHovBranchId, MpmNHovExecutionConditionState>;
}

function normalizeMpmNHovExecutionConditions(
  value: unknown,
): Record<NHovBranchId, MpmNHovExecutionConditionState> {
  const source = isRecord(value) ? value : {};
  return Object.fromEntries(
    NHOV_BRANCH_IDS.map((branchId) => [
      branchId,
      normalizeMpmNHovExecutionConditionState(source[branchId]),
    ]),
  ) as Record<NHovBranchId, MpmNHovExecutionConditionState>;
}

function normalizeMpmNHovExecutionConditionState(
  value: unknown,
): MpmNHovExecutionConditionState {
  const defaults = createDefaultMpmNHovExecutionConditionState();
  const record = isRecord(value) ? value : {};
  return {
    minimumEffect: normalizeMpmNHovExecutionMinimumEffect(
      record.minimumEffect,
      defaults.minimumEffect,
    ),
    minimumTimeSeconds: normalizeMpmNHovExecutionMinimumTimeSeconds(
      record.minimumTimeSeconds,
      defaults.minimumTimeSeconds,
    ),
  };
}

function normalizeAmplifierPanelAssignments(
  value: unknown,
): HovAmplifierEntryId[] {
  return normalizeAmplifierEntryAssignments(value);
}

function normalizeAmplifierEntryAssignments(
  value: unknown,
): HovAmplifierEntryId[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const seen = new Set<HovAmplifierEntryId>();
  const normalized: HovAmplifierEntryId[] = [];
  for (const candidate of value) {
    if (typeof candidate !== "string") continue;
    if (!HOV_AMPLIFIER_ENTRY_ID_SET.has(candidate as HovAmplifierEntryId)) {
      continue;
    }
    const entryId = candidate as HovAmplifierEntryId;
    if (seen.has(entryId)) continue;
    seen.add(entryId);
    normalized.push(entryId);
  }
  return normalized;
}

function normalizeNHovExecutionAssignments(value: unknown): NHovBranchId[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const assigned = new Set<NHovBranchId>();
  for (const candidate of value) {
    if (!isNHovBranchId(candidate)) continue;
    assigned.add(candidate);
  }
  return NHOV_BRANCH_IDS.filter((branchId) => assigned.has(branchId));
}

function normalizeHovTargetAssignments(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const seen = new Set<number>();
  const normalized: number[] = [];
  for (const candidate of value) {
    if (
      typeof candidate !== "number" ||
      !Number.isInteger(candidate) ||
      candidate < 0 ||
      candidate >= HOV_CELL_COUNT ||
      seen.has(candidate)
    ) {
      continue;
    }
    seen.add(candidate);
    normalized.push(candidate);
  }
  return normalized;
}

function normalizeHovGainUpgradeAssignments(
  value: unknown,
): HovGainUpgradeMpTarget[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const seen = new Set<HovGainUpgradeMpTargetKey>();
  const normalized: HovGainUpgradeMpTarget[] = [];
  for (const candidate of value) {
    const target = normalizeHovGainUpgradeMpTarget(candidate);
    if (target === null) {
      continue;
    }
    const key = toHovGainUpgradeMpTargetKey(target);
    if (key === null || seen.has(key)) {
      continue;
    }
    seen.add(key);
    normalized.push(target);
  }
  return normalized;
}

function normalizeHovGainUpgradeMpTarget(
  value: unknown,
): HovGainUpgradeMpTarget | null {
  if (value === "global") {
    return "global";
  }

  if (typeof value === "string") {
    return normalizeHovGainUpgradeMpTargetKey(value);
  }

  if (!isRecord(value)) {
    return null;
  }
  const index = value.index;
  if (value.kind !== "cell" || typeof index !== "number") {
    return null;
  }
  if (!isHovGainUpgradeCellIndex(index)) {
    return null;
  }
  return { kind: "cell", index };
}

function normalizeHovGainUpgradeMpTargetKey(
  value: string,
): HovGainUpgradeMpTarget | null {
  if (!value.startsWith("cell:")) {
    return null;
  }
  const index = Number(value.slice("cell:".length));
  if (!isHovGainUpgradeCellIndex(index)) {
    return null;
  }
  return { kind: "cell", index };
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
